import { useEffect, useState } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button, Card, EmptyState, Input, StatusPill } from './ui';
import * as api from '../lib/api';
import { sessionTitle } from '../lib/messages';
import type { ChatMessage, Session } from '../types';

const Panel = styled(Card)`
  min-width: 0;
  min-height: 650px;
  display: flex;
  flex-direction: column;
  padding: 24px;

  @media (max-width: 760px) { min-height: 520px; padding: 18px; }
`;

const Topline = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;

  h2 {
    margin: 0;
    color: ${p => p.theme.text};
    font-family: 'Raleway', system-ui, sans-serif;
    font-size: 24px;
    font-weight: 700;
    letter-spacing: -0.015em;
  }
`;

const Sub = styled.div`
  margin-top: 3px;
  color: ${p => p.theme.muted};
  font-size: 14px;
`;

const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
`;

const ContextButton = styled.button`
  border: 1px solid ${p => p.theme.border};
  border-radius: 8px;
  background: ${p => p.theme.surfaceStrong};
  color: ${p => p.theme.primary};
  padding: 7px 10px;
  font-size: 12px;
  font-weight: 800;

  &:hover { background: ${p => p.theme.primarySoft}; }
  &:disabled { opacity: .55; cursor: not-allowed; }
`;

const ChatScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin-bottom: 18px;
  padding: 18px;
  background: ${p => p.theme.surfaceSoft};
  border: 1px solid ${p => p.theme.border};
  border-radius: 16px;
`;

const MessageBox = styled.div`
  max-width: 92%;
  margin: 12px 0;
  padding: 14px 16px;
  border: 1px solid ${p => p.theme.border};
  border-radius: 14px;
  background: ${p => p.theme.assistant};
  color: ${p => p.theme.text};
  box-shadow: ${p => p.theme.shadow};

  &.user {
    margin-left: auto;
    background: ${p => p.theme.user};
    text-align: right;
  }
`;

const Role = styled.strong`
  display: block;
  margin-bottom: 6px;
  color: ${p => p.theme.text};
  font-size: 13px;
`;

const MessageContent = styled.div`
  word-wrap: break-word;
  font-size: 14px;
  line-height: 1.55;
  text-align: left;

  p { margin: .45em 0; }
  p:first-child { margin-top: 0; }
  p:last-child { margin-bottom: 0; }
  ul, ol { margin: .45em 0; padding-left: 1.5em; }
  li { margin: .18em 0; }
  a { color: ${p => p.theme.primaryStrong}; text-decoration: underline; }

  blockquote {
    margin: .45em 0;
    padding: .3em .9em;
    border-left: 3px solid ${p => p.theme.primary};
    color: ${p => p.theme.muted};
    background: ${p => p.theme.surfaceSoft};
    border-radius: 8px;
  }

  code {
    font-size: .9em;
    background: ${p => p.theme.primarySoft};
    padding: .1em .35em;
    border-radius: 6px;
  }

  pre {
    margin: .6em 0;
    padding: 12px 14px;
    background: ${p => p.theme.codeBg};
    color: ${p => p.theme.codeText};
    border-radius: 12px;
    overflow-x: auto;
  }

  pre code { background: transparent; padding: 0; color: inherit; }
  table { border-collapse: collapse; margin: .6em 0; font-size: 13px; }
  th, td { border: 1px solid ${p => p.theme.border}; padding: 5px 8px; }
  th { background: ${p => p.theme.surfaceSoft}; }
`;

const Composer = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 640px) { flex-direction: column; }
`;

const DialogOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 120;
  display: grid;
  place-items: start center;
  padding: 88px 20px 24px;
  background: rgba(4,8,17,0.58);
`;

const Dialog = styled.div`
  width: min(760px, 100%);
  max-height: min(760px, calc(100vh - 112px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid ${p => p.theme.border};
  border-radius: 18px;
  background: ${p => p.theme.surface};
  color: ${p => p.theme.text};
  box-shadow: 0 24px 80px rgba(0,0,0,0.38);
`;

const DialogHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  border-bottom: 1px solid ${p => p.theme.border};

  h3 {
    margin: 0;
    font-family: 'Raleway', system-ui, sans-serif;
    font-size: 20px;
  }
`;

const DialogBody = styled.div`
  overflow-y: auto;
  padding: 18px 20px 22px;
  font-size: 14px;
  line-height: 1.55;

  h1, h2, h3 { margin: .75em 0 .35em; }
  h1:first-child, h2:first-child, h3:first-child { margin-top: 0; }
  p { margin: .45em 0; }
  ul, ol { padding-left: 1.4em; }
  code {
    background: ${p => p.theme.primarySoft};
    border-radius: 6px;
    padding: .1em .35em;
  }
  pre {
    overflow-x: auto;
    padding: 12px 14px;
    border-radius: 12px;
    background: ${p => p.theme.codeBg};
    color: ${p => p.theme.codeText};
  }
`;

const CloseButton = styled.button`
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border: 1px solid ${p => p.theme.border};
  border-radius: 10px;
  background: ${p => p.theme.surfaceStrong};
  color: ${p => p.theme.text};
  font-size: 18px;
  font-weight: 800;

  &:hover { background: ${p => p.theme.primarySoft}; }
`;

type Props = {
  selectedSession: Session | null;
  messages: ChatMessage[];
  input: string;
  isLoading: boolean;
  isHistoryLoading: boolean;
  onInputChange: (value: string) => void;
  onSend: () => void;
};

export function ChatPanel({ selectedSession, messages, input, isLoading, isHistoryLoading, onInputChange, onSend }: Props) {
  const [contextOpen, setContextOpen] = useState(false);
  const [contextMarkdown, setContextMarkdown] = useState('');
  const [contextError, setContextError] = useState('');
  const [isContextLoading, setIsContextLoading] = useState(false);
  const title = selectedSession ? sessionTitle(selectedSession) : 'No session selected';
  const isReadonly = Boolean(selectedSession?.parentID);

  useEffect(() => {
    if (!contextOpen || !selectedSession?.id) return;
    let active = true;
    setIsContextLoading(true);
    setContextError('');
    setContextMarkdown('');
    api.getSessionContext(selectedSession.id)
      .then(response => {
        if (!active) return;
        setContextMarkdown(response.contextMarkdown || 'No context saved for this session.');
      })
      .catch(error => {
        if (!active) return;
        setContextMarkdown('');
        setContextError(error.response?.data?.error || error.message || 'Unable to load context');
      })
      .finally(() => {
        if (active) setIsContextLoading(false);
      });
    return () => { active = false; };
  }, [contextOpen, selectedSession?.id]);

  return (
    <Panel>
      <Topline>
        <div>
          <h2>{title}</h2>
          <Sub>{isReadonly ? 'Read-only subsession' : selectedSession ? 'Ready to chat' : 'Pick a session from the sidebar'}</Sub>
        </div>
        <HeaderActions>
          <ContextButton type="button" disabled={!selectedSession} onClick={() => setContextOpen(true)}>Context</ContextButton>
          <StatusPill $ok={Boolean(selectedSession && !isReadonly)}>{isReadonly ? 'Read only' : selectedSession ? 'Active' : 'Idle'}</StatusPill>
        </HeaderActions>
      </Topline>

      {contextOpen && selectedSession && (
        <DialogOverlay onMouseDown={() => setContextOpen(false)}>
          <Dialog role="dialog" aria-modal="true" aria-label="Session context" onMouseDown={event => event.stopPropagation()}>
            <DialogHeader>
              <div>
                <h3>Context</h3>
                <Sub>{title}</Sub>
              </div>
              <CloseButton type="button" aria-label="Close context" onClick={() => setContextOpen(false)}>x</CloseButton>
            </DialogHeader>
            <DialogBody>
              {isContextLoading && <em>Loading context...</em>}
              {!isContextLoading && contextError && <em>{contextError}</em>}
              {!isContextLoading && !contextError && (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{ a: ({ children, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer">{children}</a> }}
                >
                  {contextMarkdown}
                </ReactMarkdown>
              )}
            </DialogBody>
          </Dialog>
        </DialogOverlay>
      )}

      {selectedSession ? (
        <>
          <ChatScroll>
            {isHistoryLoading && <MessageBox><em>Loading history...</em></MessageBox>}
            {messages.map(message => (
              <MessageBox key={message.id || `${message.role}-${message.timestamp}`} className={message.role}>
                <Role>{message.role === 'user' ? 'You' : 'Assistant'}</Role>
                <MessageContent>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{ a: ({ children, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer">{children}</a> }}
                  >
                    {message.content || ''}
                  </ReactMarkdown>
                </MessageContent>
              </MessageBox>
            ))}
            {isLoading && <MessageBox><em>Thinking...</em></MessageBox>}
          </ChatScroll>
          <Composer>
            <Input
              value={input}
              onChange={event => onInputChange(event.target.value)}
              placeholder={isReadonly ? 'Chat is disabled on subsessions (read-only)' : 'Type a message...'}
              onKeyDown={event => { if (event.key === 'Enter') onSend(); }}
              disabled={isReadonly}
            />
            <Button onClick={onSend} disabled={isLoading || isReadonly}>Send</Button>
          </Composer>
        </>
      ) : (
        <EmptyState>Select a session to start chatting. Dashboard is warmed up, coffee not included.</EmptyState>
      )}
    </Panel>
  );
}
